/**
 * ConditionForm.js — Type-specific form for editing a condition
 *
 * Renders fields appropriate to the condition type:
 * resource_available, has_token, not_active, is_active, not_on_cooldown,
 * hp_below, level_at_least, has_character_flag, not_has_character_flag,
 * has_ancestry, has_class
 */

import { useId } from 'react';
import { VALID_CONDITION_TYPES } from '../../../../engine/schema';
import { cn } from '../../../../lib/utils';

const inputClass = cn(
  'bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm',
  'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none w-full'
);

const labelClass = 'text-sm font-medium text-gray-300 mb-1 block';

const ConditionForm = ({ condition, onUpdate }) => {
  const baseId = useId();
  const handleTypeChange = (newType) => {
    // Reset fields when type changes
    const base = { type: newType };
    switch (newType) {
      case 'resource_available': onUpdate({ ...base, resource: 'stress' }); break;
      case 'has_token': onUpdate({ ...base, tokenId: '' }); break;
      case 'not_active':
      case 'is_active': onUpdate({ ...base, stateKey: '' }); break;
      case 'not_on_cooldown': onUpdate({ ...base, cooldownKey: '' }); break;
      case 'hp_below': onUpdate({ ...base, threshold: 5 }); break;
      case 'level_at_least': onUpdate({ ...base, level: 1 }); break;
      case 'has_character_flag':
      case 'not_has_character_flag': onUpdate({ ...base, flag: '' }); break;
      case 'has_ancestry': onUpdate({ ...base, ancestry: '' }); break;
      case 'has_class': onUpdate({ ...base, class: '' }); break;
      default: onUpdate(base);
    }
  };

  const renderFields = () => {
    switch (condition.type) {
      case 'resource_available':
        return (
          <div>
            <label htmlFor={`${baseId}-resource`} className={labelClass}>Resource</label>
            <select
              id={`${baseId}-resource`}
              value={condition.resource || 'stress'}
              onChange={(e) => onUpdate({ ...condition, resource: e.target.value })}
              className={inputClass}
            >
              <option value="stress">Stress</option>
              <option value="hope">Hope</option>
              <option value="hp">HP</option>
            </select>
            <label htmlFor={`${baseId}-amount`} className={cn(labelClass, 'mt-2')}>Amount (optional)</label>
            <input
              id={`${baseId}-amount`}
              type="number"
              min={1}
              value={condition.amount ?? ''}
              onChange={(e) => onUpdate({ ...condition, amount: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="Any available"
              className={inputClass}
            />
          </div>
        );
      case 'has_token':
        return (
          <div>
            <label htmlFor={`${baseId}-token-id`} className={labelClass}>Token ID</label>
            <input
              id={`${baseId}-token-id`}
              type="text"
              value={condition.tokenId || ''}
              onChange={(e) => onUpdate({ ...condition, tokenId: e.target.value })}
              placeholder="e.g. rage-tokens"
              className={inputClass}
            />
          </div>
        );
      case 'not_active':
      case 'is_active':
        return (
          <div>
            <label htmlFor={`${baseId}-state-key`} className={labelClass}>State Key</label>
            <input
              id={`${baseId}-state-key`}
              type="text"
              value={condition.stateKey || ''}
              onChange={(e) => onUpdate({ ...condition, stateKey: e.target.value })}
              placeholder="e.g. absorb-active"
              className={inputClass}
            />
          </div>
        );
      case 'not_on_cooldown':
        return (
          <div>
            <label htmlFor={`${baseId}-cooldown-key`} className={labelClass}>Cooldown Key</label>
            <input
              id={`${baseId}-cooldown-key`}
              type="text"
              value={condition.cooldownKey || ''}
              onChange={(e) => onUpdate({ ...condition, cooldownKey: e.target.value })}
              placeholder="e.g. ability-cooldown"
              className={inputClass}
            />
          </div>
        );
      case 'hp_below':
        return (
          <div>
            <label htmlFor={`${baseId}-threshold`} className={labelClass}>HP Threshold</label>
            <input
              id={`${baseId}-threshold`}
              type="number"
              min={1}
              value={condition.threshold ?? 5}
              onChange={(e) => onUpdate({ ...condition, threshold: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'level_at_least':
        return (
          <div>
            <label htmlFor={`${baseId}-level`} className={labelClass}>Minimum Level</label>
            <input
              id={`${baseId}-level`}
              type="number"
              min={1}
              max={10}
              value={condition.level ?? 1}
              onChange={(e) => onUpdate({ ...condition, level: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'has_character_flag':
      case 'not_has_character_flag':
        return (
          <div>
            <label htmlFor={`${baseId}-flag`} className={labelClass}>Flag Name</label>
            <input
              id={`${baseId}-flag`}
              type="text"
              value={condition.flag || ''}
              onChange={(e) => onUpdate({ ...condition, flag: e.target.value })}
              placeholder="e.g. beast-form-active"
              className={inputClass}
            />
          </div>
        );
      case 'has_ancestry':
        return (
          <div>
            <label htmlFor={`${baseId}-ancestry`} className={labelClass}>Ancestry</label>
            <input
              id={`${baseId}-ancestry`}
              type="text"
              value={condition.ancestry || ''}
              onChange={(e) => onUpdate({ ...condition, ancestry: e.target.value })}
              placeholder="e.g. Firbolg"
              className={inputClass}
            />
          </div>
        );
      case 'has_class':
        return (
          <div>
            <label htmlFor={`${baseId}-class`} className={labelClass}>Class</label>
            <input
              id={`${baseId}-class`}
              type="text"
              value={condition.class || ''}
              onChange={(e) => onUpdate({ ...condition, class: e.target.value })}
              placeholder="e.g. Guardian"
              className={inputClass}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`${baseId}-type`} className={labelClass}>Condition Type</label>
        <select
          id={`${baseId}-type`}
          value={condition.type || ''}
          onChange={(e) => handleTypeChange(e.target.value)}
          className={inputClass}
        >
          {VALID_CONDITION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
      {renderFields()}
    </div>
  );
};

export default ConditionForm;
